import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { adminApi } from '../services/api';
import SafeIcon from '../common/SafeIcon';
import { FiInbox, FiSearch, FiGlobe, FiMail, FiPhone } from 'react-icons/fi';
import { format } from 'date-fns';

// Date formatting helper
function formatDisplayDate(value) {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const day = String(date.getDate()).padStart(2, "0");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const weekdays = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  return `${day}-${months[date.getMonth()]}-${date.getFullYear()}, ${weekdays[date.getDay()]}`;
}

export default function AdminLeads() {
  const { adminUser } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchLeads = async () => {
      try {
        const res = await adminApi.getRecentLeads(adminUser.admin_session_token);
        if (res.success) setLeads(res.leads || []);
      } catch (e) {
        console.error('Error fetching leads:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchLeads();
  }, [adminUser]);

  const filtered = leads.filter(l => 
    `${l.client_business_name} ${l.visitor_name} ${l.visitor_email} ${l.visitor_phone}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return (
    <div className="animate-pulse space-y-8">
      <div className="h-10 w-48 bg-slate-200 rounded-lg"></div>
      <div className="h-[500px] bg-surface rounded-xl"></div>
    </div>
  );

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-3xl font-bold text-ink tracking-tight">Lead Vault</h1>
        <p className="text-sm text-muted">Central archive of all submissions</p>
      </header>

      <div className="bg-surface rounded-xl p-3 shadow-card border border-border flex items-center gap-3">
        <div className="flex-grow relative group">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-primary transition-colors">
            <SafeIcon icon={FiSearch} />
          </div>
          <input 
            type="text" 
            value={searchTerm} 
            onChange={e => setSearchTerm(e.target.value)} 
            placeholder="Search all leads..." 
            className="w-full pl-12 pr-4 py-3 bg-slate-50 rounded-lg text-sm font-medium outline-none border-2 border-transparent focus:border-primary/30 focus:ring-2 focus:ring-primary/10 transition-all" 
          />
        </div>
      </div>

      <div className="bg-surface rounded-xl shadow-card border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[1100px]">
            <thead>
              <tr className="bg-slate-50/50 border-b border-border">
                <th className="py-4 px-6 text-xs font-semibold text-muted uppercase tracking-wide">Submission</th>
                <th className="py-4 px-6 text-xs font-semibold text-muted uppercase tracking-wide">Client</th>
                <th className="py-4 px-6 text-xs font-semibold text-muted uppercase tracking-wide">Visitor</th>
                <th className="py-4 px-6 text-xs font-semibold text-muted uppercase tracking-wide">Message</th>
                <th className="py-4 px-6 text-xs font-semibold text-muted uppercase tracking-wide text-center">Alerts</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map(lead => (
                <tr key={lead.lead_id} className="group hover:bg-slate-50/50 transition-all">
                  <td className="py-4 px-6">
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-ink">{formatDisplayDate(lead.submitted_at)}</span>
                      <span className="text-xs text-muted mt-0.5">{format(new Date(lead.submitted_at), 'h:mm a')}</span>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <span className="text-sm font-medium text-primary">{lead.client_business_name}</span>
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-ink">{lead.visitor_name}</span>
                      <span className="text-xs text-muted">{lead.visitor_email}</span>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <p className="text-sm text-slate-600 max-w-[300px] truncate">"{lead.message}"</p>
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex justify-center gap-1.5">
                      <div className={`w-2 h-2 rounded-full ${lead.business_email_sent ? 'bg-success' : 'bg-slate-200'}`} title="Email Sent"></div>
                      <div className={`w-2 h-2 rounded-full ${lead.sms_sent ? 'bg-success' : 'bg-slate-200'}`} title="SMS Sent"></div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}